import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useCurrentOrganization } from '../hooks/useCurrentOrganization';
import { useUserOrganizations } from '../hooks/useUserOrganizations';
import { useUserRole } from '../hooks/useUserRole';
import { supabaseOrganizationContext } from '../infrastructure/supabaseOrganizationContext';
import { organizationSwitchService } from '../services/organizationSwitch.service';
import { orgKeys } from '../services/organization.keys';
import type { Organization, UserOrganization, OrganizationContextValue } from '../types/organization.types';

export type { Organization, UserOrganization, OrganizationContextValue };

const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined);

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: currentOrganization, isLoading: isLoadingCurrentOrg } = useCurrentOrganization();
  const { data: userOrganizations, isLoading: isLoadingUserOrgs } = useUserOrganizations();
  const { data: userRoleData } = useUserRole(currentOrganization?.id);
  const currentUserRole = userRoleData?.role ?? null;

  const [isSwitchingOrganization, setIsSwitchingOrganization] = useState(false);

  const isLoading = isLoadingCurrentOrg || isLoadingUserOrgs;

  // Safe tenant switching method
  const switchOrganization = useCallback(
    async (targetOrgId: string) => {
      if (!userOrganizations || isSwitchingOrganization) return;
      if (currentOrganization?.id === targetOrgId) return;

      setIsSwitchingOrganization(true);

      try {
        await organizationSwitchService.switchOrganization({
          targetOrgId,
          previousOrgId: currentOrganization?.id,
          userOrganizations,
          port: supabaseOrganizationContext,
          queryClient,
        });

        toast({
          title: 'Organization Switched',
          description: 'Switched active organization context successfully.',
        });
      } catch (error: any) {
        console.error('Failed to switch organization:', error);
        toast({
          title: 'Switch Failed',
          description: error?.message || 'Could not switch organization. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsSwitchingOrganization(false);
      }
    },
    [userOrganizations, isSwitchingOrganization, currentOrganization?.id, queryClient, toast]
  );

  // Redirect to onboarding if user has no organizations (and not already on onboarding)
  useEffect(() => {
    if (
      !isLoading &&
      userOrganizations &&
      userOrganizations.length === 0 &&
      location.pathname !== '/onboarding'
    ) {
      navigate('/onboarding', { replace: true });
    }
  }, [isLoading, userOrganizations, navigate, location.pathname]);

  // Bootstrap race resolution: current-organization resolved null before user_organizations settled
  useEffect(() => {
    if (!isLoading && currentOrganization === null && userOrganizations?.length) {
      queryClient.invalidateQueries({ queryKey: orgKeys.current() });
    }
  }, [currentOrganization, userOrganizations, isLoading, queryClient]);

  const value: OrganizationContextValue = {
    currentOrganization,
    userOrganizations,
    currentUserRole,
    isLoading,
    isSwitchingOrganization,
    switchOrganization,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = (): OrganizationContextValue => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

export const useOrganizationContext = useOrganization;
